import WidgetTooltip from 'components/ui-blocks/widget/WidgetTooltip';
import { DEFAULT_WIDGET_OPTIONS } from 'constants/widgets';
import { isNull } from 'lodash-es';
import React, { useCallback, useMemo } from 'react';
import type { TooltipProps } from 'recharts';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CurveType } from 'recharts/types/shape/Curve';
import type { DataKey } from 'recharts/types/util/types';
import { extractSingleCurrency } from 'redux/modules/invoice.utils';
import type {
  DataItemBillingSummary,
  GraphMetadata,
  WidgetOptions,
} from 'redux/modules/superreports.types';
import { WidgetDataType } from 'redux/modules/superreports.types';
import { cssVariables } from 'styles';
import { notNull } from 'utils/typesafe';
import NoData from '../NoData';
import styles from './TimelineWidget.module.scss';
import TimelineWidgetLoading from './TimelineWidgetLoading';

interface ElementProps {
  dataKey: DataKey<any>;
  fill?: string;
  key?: string;
  stackId?: string | number;
}

const graphProps = {
  xAxis: {
    minTickGap: 25,
    axisLine: {
      strokeWidth: 0,
      width: 5,
      margin: 10,
      maxWidth: 5,
    },
    tickLine: {
      stroke: 'transparent',
    },
  },
  yAxis: {
    axisLine: {
      strokeWidth: 0,
      width: 5,
      margin: 10,
      maxWidth: 5,
    },
    tickLine: {
      stroke: 'transparent',
    },
  },
  cartesianGrid: {
    strokeWidth: 1,
    stroke: 'var(--theme_color_content_divider_subtle)',
    vertical: false,
  },
  tooltip: {
    // ref https://github.com/recharts/recharts/issues/170 -- y u no work?
    cursor: false,
    // cursor: {
    //   fill: '#9EA3C7',
    //   fillOpacity: 0.1,
    // },
    animationDuration: 100,
  },
  barchart: {
    margin: {
      top: 5,
      left: -10,
      right: 0,
      bottom: 0,
    },
  },
  bar: {
    barSize: 12,
    strokeWidth: 0,
    fill: '#999FC3',
    fillOpacity: 1,
    strokeOpacity: 1,
    animationBegin: 0,
    animationDuration: 250,
    type: 'monotone',
  },
  area: {
    animationBegin: 0,
    animationDuration: 250,
    fill: '#999FC3',
    fillOpacity: 1,
    strokeWidth: 0,
    type: 'monotone' as CurveType,
  },
  budgetLine: {
    stroke: '#e86161',
    strokeWidth: 3,
  },
  medianLine: {
    stroke: '#38314466',
    strokeWidth: 2,
  },
};

interface Props {
  billedState: boolean | null;
  data?: Array<any>;
  metadata?: GraphMetadata;
  showBudgetLine?: boolean;
  showCumulative?: boolean;
  showMedian?: boolean;

  options?: WidgetOptions;
  isLoading: boolean;
}

const TimelineWidgetBody: React.FC<Props> = ({
  billedState,
  data = [],
  metadata,
  showCumulative,
  showBudgetLine,
  showMedian,
  options = DEFAULT_WIDGET_OPTIONS,
  isLoading,
}) => {
  const showBillableNonBillable = !!options?.showBillableNonBillable;
  const showBilledUnbilled = isNull(billedState) && metadata?.showBilledUnbilled;
  const singleCurrency = extractSingleCurrency(data);

  const renderTooltip = useCallback(
    ({ payload }: TooltipProps<number, string>) => {
      const tooltip = payload?.[0]?.payload;
      if (!tooltip) return null;

      let value: number;
      let valueType: WidgetDataType;
      let billingSummary: DataItemBillingSummary | undefined;
      if (options.dataType === WidgetDataType.Money) {
        value = tooltip.cost;
        valueType = WidgetDataType.Money;
        billingSummary =
          showBilledUnbilled && value
            ? {
                billedAmount: tooltip.billedCost,
                billedPercent: (tooltip.billedCost / value) * 100,
                unbilledAmount: tooltip.unbilledCost,
                unbilledPercent: (tooltip.unbilledCost / value) * 100,
              }
            : undefined;
      } else {
        value = tooltip.duration;
        valueType = WidgetDataType.Hours;
        billingSummary =
          showBillableNonBillable && value
            ? {
                billableAmount: tooltip.billableDuration,
                billablePercent: (tooltip.billableDuration / value) * 100,
                nonBillableAmount: tooltip.nonBillableDuration,
                nonBillablePercent: (tooltip.nonBillableDuration / value) * 100,
              }
            : showBilledUnbilled && value
              ? {
                  billedAmount: tooltip.billedDuration,
                  billedPercent: (tooltip.billedDuration / value) * 100,
                  unbilledAmount: tooltip.unbilledDuration,
                  unbilledPercent: (tooltip.unbilledDuration / value) * 100,
                }
              : undefined;
      }

      return (
        <WidgetTooltip
          title={tooltip.date}
          value={value}
          valueType={valueType}
          currency={singleCurrency}
          billingSummary={billingSummary}
        />
      );
    },
    [singleCurrency, options.dataType, showBillableNonBillable, showBilledUnbilled]
  );

  const dataKey = options.dataType === WidgetDataType.Money ? 'cost' : 'duration';
  const billedDataKey =
    options.dataType === WidgetDataType.Money
      ? 'billedCost'
      : showBillableNonBillable
        ? 'billableDuration'
        : 'billedDuration';
  const unbilledDataKey =
    options.dataType === WidgetDataType.Money
      ? 'unbilledCost'
      : showBillableNonBillable
        ? 'nonBillableDuration'
        : 'unbilledDuration';

  const yAxisTickFormatter = useMemo(() => {
    const maxValue = data.reduce((max, current) => {
      return current[dataKey] > max ? current[dataKey] : max;
    }, 0);

    if (options.dataType === WidgetDataType.Hours) {
      const decimals = maxValue >= 4 ? 0 : 1;
      return (value: number) =>
        `${value.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}h`;
    }

    return (value: number) =>
      `${singleCurrency?.symbol_first ? singleCurrency.symbol : ''}${value.toLocaleString(
        undefined,
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }
      )}${(!singleCurrency?.symbol_first && singleCurrency?.symbol) || ''}`;
  }, [singleCurrency, data, dataKey, options.dataType]);

  const yAxisWidth = useMemo(() => {
    // Get the highest value of this data set to measure the needed width
    const highestValue = data.reduce((p, c) => Math.max(p, c[dataKey]), 0);

    // This gives us a fair approximation of the step value from Recharts
    const roundedUpValue = 10 ** Math.ceil(Math.log10(highestValue));
    const roundedDownValue = 10 ** Math.floor(Math.log10(highestValue));
    const niceValue = roundedUpValue > highestValue * 1.2 ? roundedDownValue * 1.2 : roundedUpValue;

    // Add an element with the appropriate font size to the DOM, measure it, and remove it again
    const m = document.createElement('span');
    m.className = styles.container;
    m.style.visibility = 'hidden';
    m.style.position = 'absolute';
    m.textContent = yAxisTickFormatter(niceValue);
    document.body.appendChild(m);
    const width = m.offsetWidth;
    document.body.removeChild(m);

    return (
      width +
      Math.abs(graphProps.yAxis.axisLine.margin * 2) +
      Math.abs(graphProps.barchart.margin.left)
    );
  }, [data, dataKey, yAxisTickFormatter]);

  if (isLoading) {
    return <TimelineWidgetLoading />;
  }

  if (options.dataType === WidgetDataType.Money && !singleCurrency && data.length) {
    return <NoData message="This widget only works with a single currency." />;
  }

  if (!data.length) {
    return <NoData style={{ height: cssVariables.widgetBaseHeight - 54 }} />;
  }

  // Cumulative graphs should automatically determine width of bars
  const barSize = showCumulative ? graphProps.bar.barSize : undefined;
  // @todo Maybe re-approach this, since ReferenceLine doesn't need budget tied to dataset.
  // Passing in budget via WidgetList maybe?
  const budget = data[0]?.budget ?? 0;
  const showBudget = showBudgetLine && budget && showCumulative;
  const canShowMedian = showMedian && metadata;

  const collapseBars = data.length > 31;

  const renderElements = (props: ElementProps | Array<ElementProps>) =>
    [props]
      .flat()
      .filter(notNull)
      .map((props, index) =>
        collapseBars ? (
          <Area key={index} {...graphProps.area} {...props} />
        ) : (
          <Bar key={index} {...graphProps.bar} {...props} barSize={barSize} />
        )
      );

  const renderBars = () => {
    if (showBillableNonBillable) {
      return renderElements([
        {
          key: 'billable',
          dataKey: billedDataKey,
          stackId: 'billableVsNonBillable',
          fill: cssVariables.widgetBillableColor,
        },
        {
          key: 'nonBillable',
          dataKey: unbilledDataKey,
          stackId: 'billableVsNonBillable',
          fill: cssVariables.widgetNonBillableColor,
        },
      ]);
    } else if (showBilledUnbilled) {
      return renderElements([
        {
          key: 'billed',
          dataKey: billedDataKey,
          stackId: 'billedVsUnbilled',
          fill: cssVariables.widgetBilledColor,
        },
        {
          key: 'unbilled',
          dataKey: unbilledDataKey,
          stackId: 'billedVsUnbilled',
          fill: cssVariables.widgetUnbilledColor,
        },
      ]);
    } else {
      return renderElements({ dataKey });
    }
  };

  const ChartComponent = collapseBars ? AreaChart : BarChart;

  return (
    <div className={styles.container}>
      <ResponsiveContainer height={cssVariables.widgetBaseHeight}>
        <ChartComponent {...graphProps.barchart} data={data}>
          <XAxis dataKey="label" {...graphProps.xAxis} />
          {budget ? (
            <YAxis domain={[0, budget]} {...graphProps.yAxis} tickFormatter={yAxisTickFormatter} />
          ) : (
            <YAxis {...graphProps.yAxis} tickFormatter={yAxisTickFormatter} width={yAxisWidth} />
          )}

          <CartesianGrid {...graphProps.cartesianGrid} />
          <Tooltip content={renderTooltip} {...graphProps.tooltip} />
          {renderBars()}
          {showBudget && <ReferenceLine y={budget} {...graphProps.budgetLine} />}
          {canShowMedian && <ReferenceLine y={metadata!.median} {...graphProps.medianLine} />}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

export default React.memo(TimelineWidgetBody);
